import { BetrokkenTrein, BusEvacuatie, TreinEvacuatie, useGetDossierBetrokkenTreinenById } from "@/entities/betrokken-treinen";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "@/features/dossier-details";
import { Badge, Dot, DotVariants, EmptyState, ErrorDisplay, Separated } from "@/shared/components";
import { getListPreview } from "@/shared/utils/list-preview.ts";

interface BetrokkenTreinenProps {
    dossierNummer: string;
}

export function BetrokkenTreinenSummary({ dossierNummer }: Readonly<BetrokkenTreinenProps>) {
    const { data, isPending, error } = useGetDossierBetrokkenTreinenById(dossierNummer, (treinen) =>
        treinen.filter((trein) => trein.isRelevant)
    );

    if (isPending) {
        return <DossierSummaryCardSkeleton section="BetrokkenTreinen" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van betrokken treinen" />;
    }

    const status = data.reduce(
        (total, trein) => {
            if (trein.busEvacuatie?.status !== "Geannuleerd") {
                total.geleverd += trein.busEvacuatie?.etas.length ?? 0;
                total.besteld += trein.busEvacuatie?.aantalBussen ?? 0;
            }
            return total;
        },
        { geleverd: 0, besteld: 0 }
    );

    const hasContent = data?.length > 0;

    return (
        <DossierSummaryCard
            section="BetrokkenTreinen"
            status={status.besteld > 0 ? `${status.geleverd}/${status.besteld} bussen geleverd` : ""}
        >
            {hasContent ? (
                <BetrokkenTreinSummaryContent betrokkenTreinen={data} />
            ) : (
                <EmptyState className="pr-3 pl-4">Geen betrokken treinen toegevoegd</EmptyState>
            )}
        </DossierSummaryCard>
    );
}

type TvvSummaryContentProps = {
    betrokkenTreinen: BetrokkenTrein[];
};

function BetrokkenTreinSummaryContent({ betrokkenTreinen }: Readonly<TvvSummaryContentProps>) {
    const { previewItems: displayTreinen, remainder } = getListPreview(betrokkenTreinen, 5);

    return (
        <>
            <Separated>
                {displayTreinen.map((trein) => (
                    <BetrokkenTreinSummaryItem key={`${trein.id}-${trein.treinNummer}`} trein={trein} />
                ))}
            </Separated>
            {remainder > 0 && <span className="text-s text-content-subtle">nog {remainder} treinen</span>}
        </>
    );
}

type BetrokkenTreinSummaryItemProps = {
    trein: BetrokkenTrein;
};

function BetrokkenTreinSummaryItem({ trein }: Readonly<BetrokkenTreinSummaryItemProps>) {
    return (
        <div className="grid grid-cols-[1fr_min-content] items-center gap-x-2 py-2 pr-3 pl-4">
            <div className="flex items-center gap-2">
                <span className="text-content-emphasis text-m font-medium">{BetrokkenTrein.formatTrainLabel(trein)}</span>
                {trein.handelingsVrij != null && (
                    <>
                        <Dot size="s" variant="emphasis" />
                        <Badge variant={trein.handelingsVrij ? "green-subtle" : "red-subtle"} className="mr-0.5">
                            {trein.handelingsVrij ? "Handelingsvrij" : "Niet handelingsvrij"}
                        </Badge>
                    </>
                )}
            </div>
            <EvacuatieStatus busEvacuatie={trein.busEvacuatie} treinEvacuatie={trein.treinEvacuatie} />
        </div>
    );
}

type EvacuatieStatusProps = {
    busEvacuatie: BusEvacuatie | undefined;
    treinEvacuatie: TreinEvacuatie | undefined;
};

function EvacuatieStatus({ busEvacuatie, treinEvacuatie }: EvacuatieStatusProps) {
    if (!busEvacuatie && !treinEvacuatie) {
        return null;
    }

    // show busevacuatie if present and is not yet afgerond
    if (busEvacuatie && busEvacuatie.status !== "Afgerond") {
        return <BusEvacuatieStatus busEvacuatie={busEvacuatie} />;
    }

    // show treinevacuatie if there is no busevacuatie
    if (!busEvacuatie && treinEvacuatie) {
        return <TreinEvacuatieStatus treinEvacuatie={treinEvacuatie} />;
    }

    // if both are present show treinevacuatie ONLY if busevacuatie is afgerond
    if (busEvacuatie && busEvacuatie.status === "Afgerond" && treinEvacuatie && treinEvacuatie?.status === "Afgerond") {
        return <span className="text-m justify-self-end text-nowrap">Evacuatietrein afgerond: ETA: {treinEvacuatie.vertrekTijd}</span>;
    }

    return null;
}

type TreinEvacuatieStatusProps = {
    treinEvacuatie: TreinEvacuatie;
};

function TreinEvacuatieStatus({ treinEvacuatie }: TreinEvacuatieStatusProps) {
    return <span className="text-m justify-self-end text-nowrap">Evacuatietrein vertrektijd: {treinEvacuatie.vertrekTijd}</span>;
}

type BusEvacuatieStatusProps = {
    busEvacuatie: BusEvacuatie;
};

function BusEvacuatieStatus({ busEvacuatie }: BusEvacuatieStatusProps) {
    const { status, afzetStation, etaAfzetStation } = busEvacuatie;
    if (status === "Geannuleerd") {
        return (
            <div className="flex items-center gap-2 justify-self-end">
                <span className="text-m justify-self-end text-nowrap">Evacuatiebus: ingetrokken</span>
                <Dot className="justify-self-center" size="m" variant="neutral" />
            </div>
        );
    }

    if (status === "AnnuleringAangevraagd") {
        return (
            <div className="flex items-center gap-2 justify-self-end">
                <span className="text-m justify-self-end text-nowrap">Evacuatiebus: intrekking aangevraagd</span>
                <Dot className="justify-self-center" size="m" variant="info" />
            </div>
        );
    }

    if (status === "Afgerond") {
        return (
            <div className="flex items-center gap-2 justify-self-end">
                <span className="text-m justify-self-end text-nowrap">
                    Evacuatiebus afgerond: ETA {etaAfzetStation} op {afzetStation}
                </span>
                <Dot className="justify-self-center" size="m" variant="neutral" />
            </div>
        );
    }

    let dotVariant: DotVariants["variant"] = status === "Aanvraag" ? "infoOpen" : "info";
    if (busEvacuatie.etas.length >= busEvacuatie.aantalBussen) {
        dotVariant = "succes";
    }

    return (
        <div className="flex items-center gap-2 justify-self-end">
            <span className="text-m justify-self-end text-nowrap">
                Evacuatiebus: {busEvacuatie.etas.length}/{busEvacuatie.aantalBussen} geleverd
            </span>
            <Dot className="justify-self-center" size="m" variant={dotVariant} />
        </div>
    );
}
